import React, { useEffect, useState } from "react"
import Paper from "../../ui/Paper"
import Input from "../../ui/Input"
import { useDispatch, useSelector } from "react-redux"
import {
  addPriorityListsFromMatchingRSOs,
  fetchPriorityLists,
  selectCreatingPriorityList,
  selectPriorityListsByContext,
  selectPriorityListsByIds,
} from "../priorities/slice"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import styled from "styled-components"
import Label from "../../ui/Label"
import Select from "../../ui/Select"
import FormError from "../../ui/FormError"
import TextArea from "../../ui/TextArea"
import FormGroup from "../../ui/FormGroup"
import Button from "../../ui/Button"
import { Controller, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import ErrorMsg from "../../ui/ErrorMsg"
import PropTypes from "prop-types"
import { slideFromRight } from "../../ui/animations"
import { useLocation, useNavigate } from "react-router-dom"
import { parseRSOFilters } from "./filters"
import { selectAstrositesById } from "../astrosites/slice"

const Panel = styled(Paper)`
  padding: 2rem;
  overflow-y: auto;
  width: 25vw;
  min-width: 20rem;
  max-width: 40rem;
  animation: 0.3s ${slideFromRight} ease-out;
`
const FormContainer = styled.div``

/**
 * The form validator
 */

/**
 * Create a new priority list.
 * Priority lists are use to group and manage objects and their observation priority.
 * A higher priority means we care more about this list of objects.
 *
 * @param handleCancel
 * @returns {JSX.Element}
 * @constructor
 */
const AddPriorityListFromSearchResults = ({ handleCancel, totalCount }) => {
  const dispatch = useDispatch()
  const [createNewList, setCreateNewList] = useState(null)

  // we get any matching priority lists
  const priorityMatches = useSelector((state) =>
    selectPriorityListsByContext(state, "root")
  )
  // We get the full objects from the ids that match
  const priorities = useSelector((state) =>
    selectPriorityListsByIds(state, priorityMatches?.ids || [])
  )
  // We pull all the possible lists upfront (Note as we grow this may not be sustainable)
  useEffect(() => {
    dispatch(
      fetchPriorityLists({
        filter: {},
        page: { offset: 0, limit: 100 },
        context: ["root"],
      })
    )
  }, [])

  // Function to handle radio button change to pick add to existing list or create new
  const handleSelectCreationMode = (event) => {
    // Update state based on which radio button was clicked
    const newValue = event.target.value === "true" // Converts the string to a boolean
    setCreateNewList(newValue)
  }

  return (
    <Panel>
      <h2>Add {totalCount} matching results</h2>
      <div>
        <FormContainer>
          <form>
            <Label htmlFor="trueOption">New list:</Label>
            <input
              type="radio"
              id="trueOption"
              name="booleanValue"
              value="true"
              checked={createNewList === true}
              onChange={handleSelectCreationMode}
            />
            <Label htmlFor="falseOption">Existing list:</Label>
            <input
              type="radio"
              id="falseOption"
              name="booleanValue"
              value="false"
              checked={createNewList === false}
              onChange={handleSelectCreationMode}
            />
          </form>
        </FormContainer>
      </div>
      {createNewList === false && (
        <AddToExistingPriorityList
          handleCancel={handleCancel}
          priorities={priorities}
        />
      )}
      {createNewList === true && (
        <AddToNewPriorityList handleCancel={handleCancel} />
      )}
    </Panel>
  )
}

/**
 * Add results of an RSO search to an existing priority list
 * @param handleCancel
 * @param priorities
 * @returns {JSX.Element}
 * @constructor
 */
const AddToExistingPriorityList = ({ handleCancel, priorities = [] }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // The creation status and data
  const inProgressList = useSelector((state) =>
    selectCreatingPriorityList(state)
  )

  // Parse parameters into filter object (which drives filters UI state)
  // Note: We are using the URL as the "state" here, and updates
  // perform a history replacement (so as not to mess up "back" button
  // behaviour too much - though we may want to apply a heuristic like
  // small changes replace history, big changes push history)
  const location = useLocation()
  const urlParams = new URLSearchParams(location.search)
  const filters = parseRSOFilters(urlParams)

  const onSubmit = (data) => {
    data.filter = filters
    dispatch(addPriorityListsFromMatchingRSOs(data))
      .unwrap()
      .then((resp) => {
        handleCancel()
        navigate(`/priorities/${resp?.id}`)
      })
  }

  const schema = yup
    .object({
      list_id: yup.string().required(),
    })
    .required()

  // configure the form
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  })

  return (
    <div>
      {" "}
      {inProgressList?._saveStatus === "creating" && (
        <Backdrop>
          <LoadingSpinner />
        </Backdrop>
      )}
      {inProgressList?._saveStatus == "rejected" && (
        <ErrorMsg
          msg={
            inProgressList._errorMessage ||
            "an error occurred creating the priority list"
          }
          showIcon={true}
        />
      )}
      <FormContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Select {...register("list_id")}>
              <option defaultValue={""} value="">
                -
              </option>
              {priorities
                ?.filter((priority) => !priority.deleted)
                .map((r, i) => (
                  <option key={i} value={r.id}>
                    {r.name}
                  </option>
                ))}
            </Select>
            <FormError>{errors.description?.message}</FormError>
          </FormGroup>
          <Button type="submit">submit</Button>
          <Button onClick={handleCancel}>close</Button>
        </form>
      </FormContainer>
    </div>
  )
}

AddToExistingPriorityList.propTypes = {
  priorities: PropTypes.array, // Cancel the creation of a priority list
  handleCancel: PropTypes.func, // Cancel the creation of a priority list
}

/**
 * Add a new priority list from the results of an RSO search
 * @param handleCancel
 * @returns {JSX.Element}
 * @constructor
 */
const AddToNewPriorityList = ({ handleCancel }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // The creation status and data
  const inProgressList = useSelector((state) =>
    selectCreatingPriorityList(state)
  )

  // Parse parameters into filter object (which drives filters UI state)
  // Note: We are using the URL as the "state" here, and updates
  // perform a history replacement (so as not to mess up "back" button
  // behaviour too much - though we may want to apply a heuristic like
  // small changes replace history, big changes push history)
  const location = useLocation()
  const urlParams = new URLSearchParams(location.search)
  const filters = parseRSOFilters(urlParams)

  const onSubmit = (data) => {
    data.filter = filters
    dispatch(addPriorityListsFromMatchingRSOs(data))
      .unwrap()
      .then((resp) => {
        handleCancel()
        navigate(`/priorities/${resp?.id}`)
      })
  }

  const schema = yup
    .object({
      name: yup.string().max(30).required(),
      description: yup.string().max(150),
      priority: yup.number().min(0).integer().required(),
      selectedAstrosites: yup.array(),
    })
    .required()

  // configure the form
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      selectedAstrosites: [],
    },
    resolver: yupResolver(schema),
  })

  const astrositeIDs = Object.keys(useSelector(selectAstrositesById))

  return (
    <div>
      {" "}
      {inProgressList?._saveStatus === "creating" && (
        <Backdrop>
          <LoadingSpinner />
        </Backdrop>
      )}
      {inProgressList?._saveStatus == "rejected" && (
        <ErrorMsg
          msg={
            inProgressList._errorMessage ||
            "an error occurred creating the priority list"
          }
          showIcon={true}
        />
      )}
      <FormContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <FormGroup>
              <Label htmlFor="name">Name:</Label>
              <Input type="text" {...register("name")} autoFocus />
              <FormError>{errors.name?.message}</FormError>
            </FormGroup>
            <FormGroup>
              <Label htmlFor="description">Description:</Label>
              <TextArea {...register("description")} rows="4" />
              <FormError>{errors.description?.message}</FormError>
            </FormGroup>
            <FormGroup>
              <Label htmlFor="priority">Priority:</Label>
              <Input type="number" defaultValue={0} {...register("priority")} />
              <FormError>{errors.priority?.message}</FormError>
            </FormGroup>
            <FormGroup>
              <Label htmlFor="selectedAstrosites">Active for:</Label>
              {astrositeIDs?.map((astrosite) => (
                <Controller
                  key={astrosite}
                  name="selectedAstrosites"
                  control={control}
                  render={({ field: { onChange, onBlur, value, ref } }) => {
                    return (
                      <label>
                        <input
                          type="checkbox"
                          ref={ref}
                          onBlur={onBlur}
                          onChange={(e) => {
                            const isChecked = e.target.checked
                            const newValue = isChecked
                              ? [...value, astrosite] // Add item if checked
                              : value?.filter((i) => i !== astrosite) // Remove item if unchecked
                            onChange(newValue) // Update the form state
                          }}
                          checked={value?.includes(astrosite)}
                        />
                        {astrosite}
                      </label>
                    )
                  }}
                />
              ))}
            </FormGroup>
          </div>

          <Button type="submit">submit</Button>
          {handleCancel && <Button onClick={handleCancel}>close</Button>}
        </form>
      </FormContainer>
    </div>
  )
}

AddToNewPriorityList.propTypes = {
  handleCancel: PropTypes.func, // Cancel the creation of a priority list
}

AddPriorityListFromSearchResults.propTypes = {
  totalCount: PropTypes.number, // the number of objects to add
  handleCancel: PropTypes.func, // Cancel the creation of a priority list
}
export default AddPriorityListFromSearchResults
